import {inject, onMounted, onUnmounted, provide, Ref, ref} from 'vue';

export type Shortcut = {
  key: string;
  label: string;
  onPress: (e: KeyboardEvent) => void;
  altKey?: boolean; // Whether the shortcut requires the Alt key
  ctrlKey?: boolean; // Whether the shortcut requires the Ctrl key
  metaKey?: boolean; // Whether the shortcut requires the Meta key (Command on Mac, Windows key on Windows)
  shiftKey?: boolean; // Whether the shortcut requires the Shift key
};

export function useKeyboardShortcuts() {
  const keyboardShortcuts = inject<Ref<Shortcut[]>>('keyboardShortcuts');

  return {
    register(shortcut: Shortcut) {
      /**
       * Do nothing if the keyboardShortcuts is not provided.
       */
      if (!keyboardShortcuts?.value) {
        return;
      }

      /**
       * Add the shortcut to the list of shortcuts.
       */
      keyboardShortcuts.value.push(shortcut);

      /**
       * Handle the keyboard event and check if the shortcut key is pressed.
       *
       * @param e
       */
      let hotkeyNavigation = (e: KeyboardEvent) => {
        if (
          e.key !== shortcut.key ||
          e.altKey != !!shortcut.altKey ||
          e.ctrlKey != !!shortcut.ctrlKey ||
          e.metaKey != !!shortcut.metaKey ||
          e.shiftKey != !!shortcut.shiftKey
        )
          return;

        let isFormInput =
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement ||
          (e.target instanceof Element &&
            e.target.closest('[contenteditable]:not([contenteditable="false"])') !== null);

        const modifierKeysPressed = shortcut.altKey || shortcut.ctrlKey || shortcut.metaKey;

        if (!modifierKeysPressed && isFormInput) {
          return;
        }

        e.stopPropagation();
        e.preventDefault();
        shortcut.onPress(e);
      };

      /**
       * Add the event listener when the component is mounted.
       */
      onMounted(() => {
        window.addEventListener('keydown', hotkeyNavigation);
      });

      /**
       * Remove the event listener when the component is unmounted.
       * Remove the shortcut from the list of shortcuts.
       */
      onUnmounted(() => {
        window.removeEventListener('keydown', hotkeyNavigation);
        keyboardShortcuts.value = keyboardShortcuts.value.filter(
          (item) => item.key !== shortcut.key
        );
      });
    },
  };
}

export function provideKeyboardShortcuts() {
  const keyboardShortcuts = ref<Shortcut[]>([]);

  provide('keyboardShortcuts', keyboardShortcuts);

  return keyboardShortcuts;
}
